/**
 * Catalog stubs for the sidebar + Packages page.
 * Ports match platforms/canvas/packages/canvas-packages (canvas.yaml).
 */

const HANDLE = { $ref: 'canvas-artifacts/agent/handle' };
const VPC = { $ref: 'canvas-artifacts/gcp/vpc' };
const SUBNET = { $ref: 'canvas-artifacts/gcp/subnet' };
const HZ_NETWORK = { $ref: 'canvas-artifacts/hetzner/network' };
const HZ_SUBNET = { $ref: 'canvas-artifacts/hetzner/subnet' };
const HZ_SERVER = { $ref: 'canvas-artifacts/hetzner/server' };

const USAGE = {
  type: 'object',
  additionalProperties: false,
  required: ['model', 'provider', 'input_tokens', 'output_tokens', 'total_tokens'],
  properties: {
    model: { type: 'string' },
    provider: { type: 'string' },
    input_tokens: { type: 'integer' },
    output_tokens: { type: 'integer' },
    total_tokens: { type: 'integer' },
    truncated_input: { type: 'boolean' },
  },
};

export const PACKAGE_STUBS = [
  {
    id: 'stub-workflow-timer',
    name: 'Timer',
    type: 'workflow/timer',
    family: 'workflow',
    inputs: { type: 'object', properties: {} },
    outputs: { type: 'object', required: ['tick'], properties: { tick: HANDLE } },
    parameters: {
      type: 'object',
      required: ['cron'],
      additionalProperties: false,
      properties: {
        cron: {
          type: 'string',
          title: 'Cron',
          default: '0 7 * * 1-5',
        },
        timezone: {
          type: 'string',
          title: 'Timezone',
          default: 'America/Los_Angeles',
        },
        label: { type: 'string', title: 'Label' },
      },
    },
  },
  {
    id: 'stub-workflow-pause',
    name: 'Pause',
    type: 'workflow/pause',
    family: 'workflow',
    inputs: { type: 'object', properties: {} },
    outputs: { type: 'object', properties: {} },
    parameters: {
      type: 'object',
      required: ['paused'],
      additionalProperties: false,
      properties: {
        paused: { type: 'boolean', title: 'Paused', default: false },
      },
    },
  },
  {
    id: 'stub-workflow-script',
    name: 'Script',
    type: 'workflow/script',
    family: 'workflow',
    inputs: { type: 'object', properties: { context: HANDLE } },
    outputs: { type: 'object', required: ['result'], properties: { result: HANDLE } },
    parameters: {
      type: 'object',
      required: ['language', 'code'],
      additionalProperties: false,
      properties: {
        language: {
          type: 'string',
          title: 'Language',
          enum: ['python', 'bash'],
          default: 'bash',
        },
        code: {
          type: 'string',
          title: 'Code',
          default: 'curl -sS -o /dev/null -w "%{http_code}\\n" https://example.com',
        },
        timeout_seconds: {
          type: 'integer',
          title: 'Timeout Seconds',
          default: 120,
        },
      },
    },
  },
  {
    id: 'stub-workflow-join',
    name: 'Join',
    type: 'workflow/join',
    family: 'workflow',
    inputs: {
      type: 'object',
      required: ['branch_a'],
      properties: {
        branch_a: HANDLE,
        branch_b: HANDLE,
        branch_c: HANDLE,
      },
    },
    outputs: {
      type: 'object',
      required: ['combined'],
      properties: { combined: HANDLE },
    },
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        reduce: {
          type: 'string',
          title: 'Reduce',
          enum: ['concat', 'json'],
          default: 'concat',
        },
      },
    },
  },
  {
    id: 'stub-workflow-placeholder',
    name: 'Placeholder',
    type: 'workflow/placeholder',
    family: 'workflow',
    inputs: { type: 'object', properties: { context: HANDLE } },
    outputs: {
      type: 'object',
      required: ['resource'],
      properties: { resource: HANDLE },
    },
    parameters: {
      type: 'object',
      required: ['name'],
      additionalProperties: false,
      properties: {
        name: { type: 'string', title: 'Name' },
        note: { type: 'string', title: 'Note' },
      },
    },
  },
  {
    id: 'stub-agent-input',
    name: 'Agent Input',
    type: 'agent/input',
    family: 'agent',
    inputs: { type: 'object', properties: {} },
    outputs: { type: 'object', properties: { handle: HANDLE } },
    parameters: {
      type: 'object',
      required: ['source'],
      additionalProperties: false,
      properties: {
        source: {
          type: 'string',
          title: 'Source',
          enum: ['file', 'inline', 'http', 'gcs'],
          default: 'inline',
        },
        text: { type: 'string', title: 'Inline text', maxLength: 8192 },
        path: { type: 'string', title: 'Local path' },
        url: { type: 'string', title: 'HTTPS URL' },
        gcs_uri: { type: 'string', title: 'GCS URI' },
        media_type: { type: 'string', title: 'Media Type', default: 'text/plain' },
      },
    },
  },
  {
    id: 'stub-agent-think',
    name: 'Agent Think',
    type: 'agent/think',
    family: 'agent',
    inputs: {
      type: 'object',
      required: ['context'],
      properties: {
        context: HANDLE,
        context_b: HANDLE,
        context_c: HANDLE,
      },
    },
    outputs: {
      type: 'object',
      required: ['thought', 'usage'],
      properties: { thought: HANDLE, usage: USAGE },
    },
    parameters: {
      type: 'object',
      required: ['prompt'],
      additionalProperties: false,
      properties: {
        prompt: { type: 'string', title: 'Prompt' },
        model: { type: 'string', title: 'Model', default: 'grok-4.5' },
        role: {
          type: 'string',
          title: 'Role',
          enum: ['qa', 'research', 'coding', 'general'],
          default: 'general',
        },
        enable_diamond: { type: 'boolean', title: 'Enable Diamond', default: false },
      },
    },
  },
  {
    id: 'stub-agent-artifact',
    name: 'Agent Artifact',
    type: 'agent/artifact',
    family: 'agent',
    inputs: {
      type: 'object',
      required: ['data'],
      properties: { data: HANDLE },
    },
    outputs: { type: 'object', properties: { handle: HANDLE } },
    parameters: {
      type: 'object',
      required: ['sink'],
      additionalProperties: false,
      properties: {
        sink: { type: 'string', title: 'Sink', enum: ['file', 'json', 'gcs'], default: 'file' },
        filename: { type: 'string', title: 'Filename', default: 'artifact.md' },
        media_type: { type: 'string', title: 'Media Type' },
      },
    },
  },
  {
    id: 'stub-agent-sink',
    name: 'Agent Sink',
    type: 'agent/sink',
    family: 'agent',
    inputs: {
      type: 'object',
      required: ['data'],
      properties: { data: HANDLE },
    },
    outputs: {
      type: 'object',
      required: ['ok'],
      properties: { ok: { type: 'boolean' }, handle: HANDLE },
    },
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: { note: { type: 'string', title: 'Note' } },
    },
  },
  {
    id: 'stub-gcp-vpc',
    name: 'GCP VPC',
    type: 'gcp/vpc',
    family: 'gcp',
    inputs: { type: 'object', properties: {} },
    outputs: { type: 'object', properties: { network: VPC } },
    parameters: {
      type: 'object',
      required: ['name'],
      additionalProperties: false,
      properties: { name: { type: 'string', title: 'Name' } },
    },
  },
  {
    id: 'stub-gcp-subnet',
    name: 'GCP Subnetwork',
    type: 'gcp/subnet',
    family: 'gcp',
    inputs: {
      type: 'object',
      required: ['network'],
      properties: { network: VPC },
    },
    outputs: { type: 'object', properties: { subnet: SUBNET } },
    parameters: {
      type: 'object',
      required: ['name', 'region', 'cidr'],
      additionalProperties: false,
      properties: {
        name: { type: 'string', title: 'Name' },
        region: { type: 'string', title: 'Region', 'x-component': 'GCPRegions' },
        cidr: { type: 'string', title: 'CIDR Block' },
      },
    },
  },
  {
    id: 'stub-gcp-gke',
    name: 'GKE Autopilot',
    type: 'gcp/gke-autopilot',
    family: 'gcp',
    inputs: {
      type: 'object',
      required: ['subnet'],
      properties: { subnet: SUBNET },
    },
    outputs: {
      type: 'object',
      properties: {
        cluster: {
          type: 'object',
          properties: { id: { type: 'string' }, endpoint: { type: 'string' } },
        },
      },
    },
    parameters: {
      type: 'object',
      required: ['name'],
      properties: {
        name: { type: 'string', title: 'Cluster Name' },
        region: {
          type: 'string',
          title: 'Region',
          description: 'Inherited from the connected subnet artifact when present',
          'x-component': 'GCPRegions',
        },
        nodeCount: { type: 'integer', title: 'Number of Nodes', default: 3 },
      },
    },
  },
  {
    id: 'stub-gcp-mysql',
    name: 'Cloud SQL MySQL',
    type: 'gcp/cloudsql-mysql',
    family: 'gcp',
    inputs: {
      type: 'object',
      required: ['network'],
      properties: { network: VPC },
    },
    outputs: {
      type: 'object',
      properties: {
        instance: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            connection_name: { type: 'string' },
          },
        },
      },
    },
    parameters: {
      type: 'object',
      required: ['name', 'region', 'tier', 'storage_size_gb'],
      properties: {
        name: { type: 'string', title: 'Instance Name' },
        region: { type: 'string', title: 'Region' },
        tier: { type: 'string', title: 'Machine Type', default: 'db-custom-1-3840' },
        storage_size_gb: { type: 'integer', title: 'Storage Size (GB)', default: 10 },
      },
    },
  },
  {
    id: 'stub-gcp-postgres',
    name: 'Cloud SQL PostgreSQL',
    type: 'gcp/cloudsql-postgres',
    family: 'gcp',
    inputs: {
      type: 'object',
      required: ['subnet'],
      properties: { subnet: SUBNET },
    },
    outputs: {
      type: 'object',
      properties: {
        instance: {
          type: 'object',
          properties: { id: { type: 'string' } },
        },
      },
    },
    parameters: {
      type: 'object',
      required: ['name', 'region', 'tier', 'storage_size_gb'],
      properties: {
        name: { type: 'string', title: 'Instance Name' },
        region: {
          type: 'string',
          title: 'Region',
          description: 'Inherited from the connected subnet artifact when present',
        },
        tier: { type: 'string', title: 'Machine Type', default: 'db-custom-1-3840' },
        storage_size_gb: { type: 'integer', title: 'Storage Size (GB)', default: 10 },
      },
    },
  },
  {
    id: 'stub-hetzner-network',
    name: 'Hetzner Network',
    type: 'hetzner/network',
    family: 'hetzner',
    inputs: { type: 'object', properties: {} },
    outputs: { type: 'object', properties: { network: HZ_NETWORK } },
    parameters: {
      type: 'object',
      required: ['name'],
      additionalProperties: false,
      properties: {
        name: {
          type: 'string',
          title: 'Name',
          pattern: '^[a-z]([-a-z0-9]*[a-z0-9])?$',
          maxLength: 63,
        },
        ip_range: { type: 'string', title: 'IP Range', default: '10.0.0.0/16' },
      },
    },
  },
  {
    id: 'stub-hetzner-subnet',
    name: 'Hetzner Subnet',
    type: 'hetzner/subnet',
    family: 'hetzner',
    inputs: {
      type: 'object',
      required: ['network'],
      properties: { network: HZ_NETWORK },
    },
    outputs: { type: 'object', properties: { subnet: HZ_SUBNET } },
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        ip_range: { type: 'string', title: 'IP Range', default: '10.0.1.0/24' },
        zone: {
          type: 'string',
          title: 'Network Zone',
          enum: ['eu-central', 'us-east', 'us-west', 'ap-southeast'],
          default: 'us-east',
        },
      },
    },
  },
  {
    id: 'stub-hetzner-server',
    name: 'Hetzner Server',
    type: 'hetzner/server',
    family: 'hetzner',
    inputs: {
      type: 'object',
      required: ['subnet'],
      properties: { subnet: HZ_SUBNET },
    },
    outputs: { type: 'object', properties: { server: HZ_SERVER } },
    parameters: {
      type: 'object',
      required: ['name', 'ssh_allowed_cidr'],
      additionalProperties: false,
      properties: {
        name: {
          type: 'string',
          title: 'Name',
          pattern: '^[a-z]([-a-z0-9]*[a-z0-9])?$',
          maxLength: 63,
        },
        server_type: { type: 'string', title: 'Server Type', default: 'cpx11' },
        image: { type: 'string', title: 'Image', default: 'ubuntu-24.04' },
        location: {
          type: 'string',
          title: 'Location',
          enum: ['fsn1', 'nbg1', 'hel1', 'ash', 'hil', 'sin'],
          default: 'ash',
        },
        ssh_pubkey: { type: 'string', title: 'SSH Public Key', 'x-component': 'textarea' },
        ssh_key_name: { type: 'string', title: 'Existing SSH Key Name', default: '' },
        ssh_allowed_cidr: { type: 'string', title: 'SSH Allowed CIDR' },
        user_data: { type: 'string', title: 'User Data', 'x-component': 'textarea' },
      },
    },
  },
];

const stubsByType = Object.fromEntries(PACKAGE_STUBS.map((s) => [s.type, s]));

export function stubForType(type) {
  return stubsByType[type] || null;
}

export function catalogPayload(stub) {
  return {
    name: stub.name,
    type: stub.type,
    inputs: stub.inputs,
    outputs: stub.outputs,
    parameters: stub.parameters,
    private: false,
  };
}
